"use client"

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { getAuthHeader } from "../utils/auth";

const API_URL = ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? (process.env.REACT_APP_API_URL || "http://localhost:3173")
    : window.location.origin;

export const FREE_PLAN_SURVEY_VERSION = 'continuation_v1';

export const PRIMARY_REASON_OPTIONS = [
    { code: 'acquisition_results', label: '新規顧客・商談を十分に増やせなかった' },
    { code: 'crm_adoption', label: '顧客管理・追客の運用が定着しなかった' },
    { code: 'operational_burden', label: '設定や日々の運用に負担があった' },
    { code: 'resource_constraints', label: '利用する時間・担当体制を確保できなかった' },
    { code: 'missing_features', label: '必要な機能・外部連携がなかった' },
    { code: 'quality_reliability', label: '送信処理・安定性・企業データ品質に不満があった' },
    { code: 'pricing_value', label: '料金が得られた価値に見合わなかった' },
    { code: 'business_change', label: '事業方針の変更・営業活動の一時休止' },
    { code: 'competitor', label: '別のサービスへ移行する' },
    { code: 'other', label: 'その他' },
];

export const CONTINUATION_CONDITION_OPTIONS = [
    { code: 'more_leads', label: '新規リードをもっと獲得できる' },
    { code: 'more_replies_meetings_sales', label: '返信・商談・受注をもっと増やせる' },
    { code: 'crm_adoption', label: 'CRMで顧客管理・追客を定着させられる' },
    { code: 'onboarding_support', label: '初期設定や日々の運用を支援してもらえる' },
    { code: 'quality_improvement', label: '送信処理や企業データの品質が改善する' },
    { code: 'feature_integration', label: '必要な機能・外部連携が追加される' },
    { code: 'right_sized_pricing', label: '利用規模に合った料金・プランがある' },
    { code: 'unavoidable', label: '何が改善されても、今回は継続しなかった' },
    { code: 'unknown', label: '何があれば継続したか分からない' },
    { code: 'other', label: 'その他' },
];

export const FUNNEL_BOTTLENECK_OPTIONS = [
    { code: 'targeting', label: '対象企業を探す・選定する' },
    { code: 'sending_reach', label: 'フォーム送信・相手企業への到達' },
    { code: 'reply', label: '返信を獲得する' },
    { code: 'meeting', label: '返信から商談につなげる' },
    { code: 'crm_follow_up', label: '顧客管理・追客を継続する' },
    { code: 'closing', label: '商談から受注につなげる' },
    { code: 'unknown', label: 'どこが課題か分からない' },
];

const IDEMPOTENCY_STORAGE_KEY = 'salesbreaker:free-plan-survey:idempotency-key';
const RETRY_PAYLOAD_STORAGE_KEY = 'salesbreaker:free-plan-survey:retry-payload';
const OUTCOME_RELATED_REASONS = new Set(['acquisition_results', 'crm_adoption']);
const OUTCOME_RELATED_CONDITIONS = new Set(['more_leads', 'more_replies_meetings_sales', 'crm_adoption']);
const EXCLUSIVE_CONTINUATION_CONDITIONS = new Set(['unavoidable', 'unknown']);

const createIdempotencyKey = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
};

const getOrCreateIdempotencyKey = () => {
    try {
        const existing = window.sessionStorage.getItem(IDEMPOTENCY_STORAGE_KEY);
        if (existing) return existing;
        const created = createIdempotencyKey();
        window.sessionStorage.setItem(IDEMPOTENCY_STORAGE_KEY, created);
        return created;
    } catch (_error) {
        return createIdempotencyKey();
    }
};

const clearIdempotencyKey = () => {
    try {
        window.sessionStorage.removeItem(IDEMPOTENCY_STORAGE_KEY);
        window.sessionStorage.removeItem(RETRY_PAYLOAD_STORAGE_KEY);
    } catch (_error) {
        // Ignore storage errors in restricted browser contexts.
    }
};

const getStoredRetryPayload = (user) => {
    try {
        const envelope = JSON.parse(window.sessionStorage.getItem(RETRY_PAYLOAD_STORAGE_KEY) || 'null');
        const sameOwner = envelope
            && String(envelope.owner_user_id || '') === String(user?.id || '')
            && String(envelope.owner_client_id || '') === String(user?.client_id || '');
        if (!sameOwner || !envelope.payload?.idempotency_key) {
            clearIdempotencyKey();
            return null;
        }
        return envelope.payload;
    } catch (_error) {
        clearIdempotencyKey();
        return null;
    }
};

const storeRetryPayload = (payload, user) => {
    try {
        window.sessionStorage.setItem(RETRY_PAYLOAD_STORAGE_KEY, JSON.stringify({
            owner_user_id: user?.id,
            owner_client_id: user?.client_id,
            payload,
        }));
    } catch (_error) {
        // The in-memory copy still protects retries in this page session.
    }
};

const formatEffectiveAt = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat('ja-JP', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Tokyo',
    }).format(date);
};

const resolveCurrentPlanName = (context) => {
    const currentPlan = context?.current_plan;
    if (typeof currentPlan === 'string') return currentPlan;
    return currentPlan?.name || currentPlan?.plan_name || context?.current_plan_name || context?.plan_name || '現在の有料プラン';
};

function FreePlanSurveyPage() {
    const navigate = useNavigate();
    const { user } = useOutletContext();
    const [context, setContext] = useState(null);
    const [contextReloadToken, setContextReloadToken] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [validationError, setValidationError] = useState('');
    const [primaryReasonCode, setPrimaryReasonCode] = useState('');
    const [continuationConditionCodes, setContinuationConditionCodes] = useState([]);
    const [funnelBottleneckCode, setFunnelBottleneckCode] = useState('');
    const [comment, setComment] = useState('');
    const initialRetryPayloadRef = useRef(getStoredRetryPayload(user));
    const retryPayloadRef = useRef(initialRetryPayloadRef.current);
    const [isRetryPending, setIsRetryPending] = useState(Boolean(initialRetryPayloadRef.current));
    const idempotencyKeyRef = useRef(initialRetryPayloadRef.current?.idempotency_key || null);
    const submissionInFlightRef = useRef(false);

    const canManageBilling = user?.role === 'admin' || user?.role === 'client_admin';

    useEffect(() => {
        const controller = new AbortController();

        const fetchContext = async () => {
            if (!canManageBilling) {
                setError('無料プランへの変更権限がありません。');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setContext(null);
            setError('');
            try {
                const response = await fetch(`${API_URL}/api/subscriptions/free-plan-survey-context`, {
                    headers: getAuthHeader(),
                    cache: 'no-store',
                    signal: controller.signal,
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data.success) {
                    throw new Error(data.error || '無料プランへの変更内容を確認できませんでした。');
                }
                setContext(data);
                if (data.eligible !== true && !(data.transition_pending && retryPayloadRef.current)) {
                    setError(data.error || '無料プランへの変更可否を確認できません。');
                }
            } catch (requestError) {
                if (requestError.name === 'AbortError') return;
                setError(requestError.message || '無料プランへの変更内容を確認できませんでした。');
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        };

        fetchContext();
        return () => controller.abort();
    }, [canManageBilling, contextReloadToken]);

    const shouldAskFunnelBottleneck = useMemo(() => {
        if (OUTCOME_RELATED_REASONS.has(primaryReasonCode)) return true;
        return continuationConditionCodes.some((code) => OUTCOME_RELATED_CONDITIONS.has(code));
    }, [primaryReasonCode, continuationConditionCodes]);

    const effectiveAtLabel = useMemo(() => formatEffectiveAt(context?.effective_at || context?.effectiveAt), [context]);
    const appliesImmediately = Boolean(context?.applies_immediately ?? context?.apply_immediately);
    const hasClearEffectiveTiming = appliesImmediately || Boolean(effectiveAtLabel);
    const isEligible = canManageBilling
        && (isRetryPending || (hasClearEffectiveTiming && context?.eligible === true));

    const handleContinuationToggle = (code) => {
        setValidationError('');
        setContinuationConditionCodes((current) => {
            if (current.includes(code)) return current.filter((item) => item !== code);
            if (EXCLUSIVE_CONTINUATION_CONDITIONS.has(code)) return [code];
            const selectableCurrent = current.filter((item) => !EXCLUSIVE_CONTINUATION_CONDITIONS.has(item));
            if (selectableCurrent.length >= 2) return selectableCurrent;
            return [...selectableCurrent, code];
        });
    };

    const validateAnsweredSurvey = () => {
        if (!primaryReasonCode) {
            return '「無料プランへ変更するきっかけ」を選択してください。';
        }
        if (continuationConditionCodes.length === 0) {
            return '「何が実現していれば継続したか」を1つ以上選択してください。';
        }
        if (shouldAskFunnelBottleneck && !funnelBottleneckCode) {
            return '「営業プロセスで詰まった場所」を選択してください。';
        }
        return '';
    };

    const submitDowngrade = async ({ skipped }) => {
        if (submissionInFlightRef.current || !isEligible) return;

        if (!retryPayloadRef.current && !skipped) {
            const formError = validateAnsweredSurvey();
            if (formError) {
                setValidationError(formError);
                return;
            }
        }

        if (!idempotencyKeyRef.current) {
            idempotencyKeyRef.current = getOrCreateIdempotencyKey();
        }

        submissionInFlightRef.current = true;
        setIsSubmitting(true);
        setError('');
        setValidationError('');

        const freshPayload = {
            survey_version: context?.survey_version ?? FREE_PLAN_SURVEY_VERSION,
            primary_reason_code: skipped ? null : primaryReasonCode,
            continuation_condition_codes: skipped ? [] : continuationConditionCodes,
            funnel_bottleneck_code: skipped || !shouldAskFunnelBottleneck ? null : funnelBottleneckCode,
            comment: skipped ? null : (comment.trim() || null),
            skipped,
            idempotency_key: idempotencyKeyRef.current,
        };
        const payload = retryPayloadRef.current || freshPayload;

        try {
            const response = await fetch(`${API_URL}/api/subscriptions/downgrade-to-free`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Idempotency-Key': payload.idempotency_key,
                    ...getAuthHeader(),
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) {
                const requestError = new Error(data.error || '無料プランへの変更に失敗しました。');
                requestError.retryWithSameKey = Boolean(
                    data.retry_with_same_key === true
                    || response.status >= 500
                );
                throw requestError;
            }

            clearIdempotencyKey();
            retryPayloadRef.current = null;
            setIsRetryPending(false);
            navigate('/account?plan_change=free', { replace: true });
        } catch (requestError) {
            if (requestError.retryWithSameKey || requestError.name === 'TypeError') {
                retryPayloadRef.current = payload;
                storeRetryPayload(payload, user);
                setIsRetryPending(true);
            } else {
                clearIdempotencyKey();
                idempotencyKeyRef.current = null;
                retryPayloadRef.current = null;
                setIsRetryPending(false);
                setContext(null);
                setContextReloadToken((current) => current + 1);
            }
            setError(requestError.message || '無料プランへの変更に失敗しました。時間をおいて再度お試しください。');
            submissionInFlightRef.current = false;
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <div className="p-8 text-center text-slate-600">無料プランへの変更内容を確認しています...</div>;
    }

    return (
        <div className="min-h-screen bg-slate-50 p-3 sm:p-8">
            <div className="mx-auto max-w-3xl">
                <button
                    type="button"
                    onClick={() => navigate('/account')}
                    disabled={isSubmitting}
                    className="mb-5 inline-flex items-center text-sm font-semibold text-slate-600 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    ← アカウント・契約管理へ戻る
                </button>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-lg sm:p-8">
                    <div className="border-b border-slate-200 pb-6">
                        <p className="text-sm font-bold text-indigo-600">継続条件調査</p>
                        <h1 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">無料プランへの変更</h1>
                        <p className="mt-3 text-sm leading-6 text-slate-600">
                            継続をお願いするためではなく、「何が実現していれば有料プランを継続できたか」を今後のサービス改善に役立てる調査です。
                        </p>
                    </div>

                    <section className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:p-5" aria-labelledby="transition-summary-title">
                        <h2 id="transition-summary-title" className="font-bold text-amber-950">変更の適用時期</h2>
                        <p className="mt-2 text-sm leading-6 text-amber-900">
                            現在のプラン：<span className="font-bold">{resolveCurrentPlanName(context)}</span>
                        </p>
                        {isRetryPending && !hasClearEffectiveTiming ? (
                            <p className="mt-1 text-sm font-semibold leading-6 text-blue-800">
                                前回送信した変更の受付結果を、保存済みの同じ操作キーで確認できます。
                            </p>
                        ) : appliesImmediately ? (
                            <p className="mt-1 text-sm font-semibold leading-6 text-amber-950">
                                この画面から送信完了後、ただちに無料プランへ変更されます。
                            </p>
                        ) : effectiveAtLabel ? (
                            <p className="mt-1 text-sm font-semibold leading-6 text-amber-950">
                                {effectiveAtLabel}に無料プランへ変更されます。それまでは現在の有料プランを利用できます。
                            </p>
                        ) : (
                            <p className="mt-1 text-sm font-semibold leading-6 text-red-700">
                                適用日時を確認できないため、現在は変更を実行できません。
                            </p>
                        )}
                        {context?.paid_options_will_be_removed !== false && (
                            <p className="mt-2 text-xs leading-5 text-amber-800">有料オプションも無料プランへの変更とあわせて停止対象になります。</p>
                        )}
                    </section>

                    {error && (
                        <div className="mt-6 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                            {error}
                            {!isRetryPending && context?.eligible !== true && (
                                <button
                                    type="button"
                                    onClick={() => setContextReloadToken((current) => current + 1)}
                                    className="mt-3 block rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-bold text-red-800 hover:bg-red-100"
                                >
                                    契約情報を再読み込み
                                </button>
                            )}
                        </div>
                    )}

                    {isRetryPending && (
                        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" role="status">
                            最初の変更リクエストが決済側へ届いた可能性があります。回答内容は変更せず、下のボタンで受付状態を再確認します。
                        </div>
                    )}

                    <form className="mt-8 space-y-9" onSubmit={(event) => { event.preventDefault(); submitDowngrade({ skipped: false }); }}>
                        <fieldset disabled={isSubmitting || !isEligible || isRetryPending}>
                            <legend className="text-lg font-bold text-slate-900">
                                1. 無料プランへ変更する一番のきっかけを教えてください。
                            </legend>
                            <p className="mt-1 text-xs text-slate-500">回答する場合は1つ選択</p>
                            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {PRIMARY_REASON_OPTIONS.map((option) => (
                                    <label key={option.code} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 text-sm leading-6 transition ${primaryReasonCode === option.code ? 'border-indigo-500 bg-indigo-50 text-indigo-950' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`}>
                                        <input
                                            type="radio"
                                            name="primary-reason"
                                            value={option.code}
                                            checked={primaryReasonCode === option.code}
                                            onChange={() => { setPrimaryReasonCode(option.code); setValidationError(''); }}
                                            className="mt-1 h-4 w-4 shrink-0 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        </fieldset>

                        <fieldset disabled={isSubmitting || !isEligible || isRetryPending}>
                            <legend className="text-lg font-bold text-slate-900">
                                2. 何が実現していれば、有料プランを継続した可能性が高いですか？
                            </legend>
                            <p className="mt-1 text-xs text-slate-500">最大2つまで（{continuationConditionCodes.length}/2）</p>
                            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {CONTINUATION_CONDITION_OPTIONS.map((option) => {
                                    const checked = continuationConditionCodes.includes(option.code);
                                    const hasExclusiveSelection = continuationConditionCodes.some((code) => EXCLUSIVE_CONTINUATION_CONDITIONS.has(code));
                                    const selectionLimitReached = continuationConditionCodes.length >= 2
                                        && !checked
                                        && !hasExclusiveSelection
                                        && !EXCLUSIVE_CONTINUATION_CONDITIONS.has(option.code);
                                    return (
                                        <label key={option.code} className={`flex items-start gap-3 rounded-xl border p-4 text-sm leading-6 transition ${checked ? 'border-indigo-500 bg-indigo-50 text-indigo-950' : selectionLimitReached ? 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400' : 'cursor-pointer border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`}>
                                            <input
                                                type="checkbox"
                                                value={option.code}
                                                checked={checked}
                                                disabled={isSubmitting || !isEligible || selectionLimitReached}
                                                onChange={() => handleContinuationToggle(option.code)}
                                                className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                            <span>{option.label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </fieldset>

                        {shouldAskFunnelBottleneck && (
                            <fieldset disabled={isSubmitting || !isEligible || isRetryPending} className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-4 sm:p-5">
                                <legend className="px-1 text-lg font-bold text-slate-900">
                                    3. 営業プロセスのどこで一番詰まりましたか？
                                </legend>
                                <p className="mt-1 text-xs text-slate-500">成果に関する回答のみ表示しています。1つ選択</p>
                                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    {FUNNEL_BOTTLENECK_OPTIONS.map((option) => (
                                        <label key={option.code} className="flex cursor-pointer items-start gap-3 rounded-lg bg-white px-3 py-3 text-sm text-slate-700 shadow-sm ring-1 ring-slate-200">
                                            <input
                                                type="radio"
                                                name="funnel-bottleneck"
                                                value={option.code}
                                                checked={funnelBottleneckCode === option.code}
                                                onChange={() => { setFunnelBottleneckCode(option.code); setValidationError(''); }}
                                                className="mt-0.5 h-4 w-4 shrink-0 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                            <span>{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </fieldset>
                        )}

                        <div>
                            <label htmlFor="free-plan-survey-comment" className="text-lg font-bold text-slate-900">
                                SalesBreakerに一つだけ変えてほしかったことがあれば教えてください。
                            </label>
                            <p className="mt-1 text-xs text-slate-500">任意・500文字まで</p>
                            <textarea
                                id="free-plan-survey-comment"
                                value={comment}
                                onChange={(event) => setComment(event.target.value)}
                                maxLength={500}
                                rows={5}
                                disabled={isSubmitting || !isEligible || isRetryPending}
                                className="mt-3 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-100"
                                placeholder="例：CRMの追客方法を最初に案内してほしかった"
                            />
                            <p className="mt-1 text-right text-xs text-slate-400">{comment.length}/500</p>
                        </div>

                        {validationError && (
                            <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                                {validationError}
                            </div>
                        )}

                        <div className="border-t border-slate-200 pt-6">
                            <p className="mb-4 text-sm leading-6 text-slate-600">
                                {isRetryPending
                                    ? '前回と同じ操作キー・回答内容で、変更の受付状態だけを再確認します。'
                                    : '以下のどちらかのボタンを押すと、無料プランへの変更処理が実行されます。アンケートに回答せず変更することもできます。'}
                            </p>
                            <div className="flex flex-col gap-3 sm:flex-row">
                                <button
                                    type="submit"
                                    disabled={isSubmitting || !isEligible}
                                    className="inline-flex min-h-12 flex-1 items-center justify-center rounded-lg bg-red-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                                >
                                    {isSubmitting ? '変更処理中...' : isRetryPending ? '変更の受付状態を再確認' : '回答を送信して無料プランへ変更'}
                                </button>
                                {!isRetryPending && (
                                    <button
                                        type="button"
                                        onClick={() => submitDowngrade({ skipped: true })}
                                        disabled={isSubmitting || !isEligible}
                                        className="inline-flex min-h-12 flex-1 items-center justify-center rounded-lg border border-slate-400 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                                    >
                                        {isSubmitting ? '変更処理中...' : '回答せず無料プランへ変更'}
                                    </button>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate('/account')}
                                disabled={isSubmitting}
                                className="mt-4 w-full rounded-lg px-4 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                変更せず戻る
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default FreePlanSurveyPage;
